import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateVoice } from '../hooks';
import { Icon } from '@/shared/icons';
import { Breadcrumb } from '@/shared/components/ui';
import { Button } from '@/shared/components/ui/Button';
import { FormSection } from '@/shared/components/ui/FormSection';
import { FormField } from '@/shared/components/ui/FormField';
import { Input } from '@/shared/components/ui/Input';
import { Textarea } from '@/shared/components/ui/Textarea';
import { Checkbox } from '@/shared/components/ui/Checkbox';

/** Formato legível de tamanho de arquivo (dado real do File). */
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** Formata segundos em m:ss (tempo do player, estilo DS). */
const formatTime = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

/** Alturas decorativas das 48 barras do waveform (fórmula determinística do mockup voice-form). */
const WAVE_BARS = Array.from(
  { length: 48 },
  (_, i) => 4 + Math.round(10 * Math.abs(Math.sin(i * 0.55)) + 6 * Math.abs(Math.sin(i * 0.23))),
);

export const VoiceFormPage: React.FC = () => {
  const navigate = useNavigate();
  const createVoice = useCreateVoice();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [removeBackgroundNoise, setRemoveBackgroundNoise] = useState(false);
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  // Playback state (player customizado com waveform — substitui <audio controls> nativo)
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      void el.play();
    } else {
      el.pause();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAudioFile(file);
      setAudioUrl(URL.createObjectURL(file));
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : 'audio/webm',
      });

      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const file = new File([blob], 'voice-recording.webm', { type: 'audio/webm' });
        setAudioFile(file);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
    } catch {
      // Silently handle - user denied microphone access
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const clearAudio = () => {
    setAudioFile(null);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    audioRef.current?.pause();
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  };

  const handleSave = async () => {
    if (!name.trim() || !audioFile) return;

    const formData = new FormData();
    formData.append('file', audioFile);
    formData.append('name', name.trim());
    if (description.trim()) {
      formData.append('description', description.trim());
    }
    formData.append('removeBackgroundNoise', String(removeBackgroundNoise));

    try {
      await createVoice.mutateAsync(formData);
      navigate('/automation/voices');
    } catch {
      // Error handled by hook
    }
  };

  const canSave = Boolean(name.trim() && audioFile && !createVoice.isPending);

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* Sticky Header — breadcrumb + ações */}
      <div
        className="sticky top-0 z-10"
        style={{
          backgroundColor: 'var(--theme-card-background)',
          borderBottom: '1px solid var(--theme-card-border)',
        }}
      >
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-3 flex items-center justify-between gap-3">
          {/* Breadcrumb shared (F25-31 — nunca seta-voltar) */}
          <Breadcrumb
            items={[
              {
                label: 'Automação',
                onNavigate: () => navigate('/automation/voices'),
                testid: 'voice-form-breadcrumb-automation',
              },
              {
                label: 'Vozes',
                onNavigate: () => navigate('/automation/voices'),
                testid: 'voice-form-breadcrumb-voices',
              },
              { label: 'Nova Voz', testid: 'voice-form-breadcrumb-current' },
            ]}
            mobileLegend="Automação · Vozes"
            data-testid="voice-form-breadcrumb"
          />

          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              variant="secondary"
              className="hidden sm:inline-flex"
              onClick={() => navigate('/automation/voices')}
              data-testid="voice-form-cancel"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={!canSave}
              loading={createVoice.isPending}
              icon="check"
              data-testid="voice-form-save"
            >
              <span className="hidden sm:inline">{createVoice.isPending ? 'Salvando...' : 'Criar Voz'}</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-8">
        <div className="space-y-6">
          {/* Informações */}
          <FormSection
            icon="mic"
            title="Informações"
            description="Clone uma voz para usar com seus agentes de IA"
          >
            <FormField label="Nome" required htmlFor="voice-form-name-input">
              <Input
                id="voice-form-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Voz do Atendente"
                data-testid="voice-form-name"
              />
            </FormField>

            <FormField label="Descrição" htmlFor="voice-form-description-input">
              <Textarea
                id="voice-form-description-input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Descreva esta voz..."
                rows={3}
                data-testid="voice-form-description"
              />
            </FormField>
          </FormSection>

          {/* Amostra de Áudio */}
          <FormSection
            icon="music"
            title="Amostra de Áudio *"
            description="Grave ou envie um áudio de pelo menos 30 segundos para clonar a voz"
          >
            {/* Estado sem áudio: gravar / enviar */}
            {!audioFile && (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-3">
                  <Button
                    variant={isRecording ? 'error' : 'secondary'}
                    size="lg"
                    icon={isRecording ? 'square' : 'mic'}
                    onClick={isRecording ? stopRecording : startRecording}
                    data-testid="voice-form-record"
                  >
                    {isRecording ? 'Parar Gravação' : 'Gravar Áudio'}
                  </Button>

                  <Button
                    variant="secondary"
                    size="lg"
                    icon="upload"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="voice-form-upload"
                  >
                    Enviar Arquivo
                  </Button>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".mp3,.wav,.ogg,.m4a,.webm"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="voice-form-file"
                  />
                </div>

                {isRecording ? (
                  <div className="flex items-center gap-2" data-testid="voice-form-recording">
                    <span
                      className="w-2.5 h-2.5 rounded-full animate-pulse"
                      style={{ backgroundColor: 'var(--theme-error-color)' }}
                    />
                    <span
                      className="text-sm font-medium"
                      style={{ color: 'var(--theme-error-color)' }}
                    >
                      Gravando...
                    </span>
                  </div>
                ) : (
                  <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                    Formatos aceitos: .mp3, .wav, .ogg, .m4a, .webm
                  </p>
                )}
              </div>
            )}

            {/* Preview do áudio */}
            {audioFile && audioUrl && (
              <div
                className="rounded-xl border p-3.5 space-y-3"
                style={{
                  backgroundColor: 'var(--theme-input-background)',
                  borderColor: 'var(--theme-input-border)',
                }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="flex items-center justify-center w-10 h-10 rounded-[11px] flex-shrink-0"
                    style={{ backgroundColor: 'var(--theme-primary-background)' }}
                  >
                    <Icon name="music" className="w-5 h-5" style={{ color: 'var(--theme-primary)' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-sm font-semibold truncate"
                      style={{ color: 'var(--theme-card-text)' }}
                    >
                      {audioFile.name}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                      {formatTime(duration)} · {formatFileSize(audioFile.size)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={clearAudio}
                    className="flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 transition-colors"
                    style={{ color: 'var(--theme-muted-text)' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = 'var(--theme-error-color)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = 'var(--theme-muted-text)';
                    }}
                    aria-label="Remover áudio"
                    data-testid="voice-form-clear-audio"
                  >
                    <Icon name="x" className="w-4 h-4" />
                  </button>
                </div>

                <div
                  className="flex items-center gap-2.5 rounded-[11px] border px-3 py-2.5"
                  style={{ borderColor: 'var(--theme-input-border)' }}
                >
                  <button
                    type="button"
                    onClick={togglePlay}
                    className="flex items-center justify-center w-9 h-9 rounded-full flex-shrink-0 transition-opacity hover:opacity-90"
                    style={{ backgroundColor: 'var(--theme-primary)', color: '#ffffff' }}
                    aria-label={isPlaying ? 'Pausar' : 'Reproduzir'}
                    data-testid="voice-form-play"
                  >
                    <Icon name={isPlaying ? 'pause' : 'play'} className="w-4 h-4" />
                  </button>
                  <div className="flex flex-1 items-center gap-0.5 h-[26px] overflow-hidden">
                    {WAVE_BARS.map((h, i) => (
                      <span
                        key={i}
                        className="flex-shrink-0 rounded-[2px]"
                        style={{
                          width: 3,
                          height: h,
                          backgroundColor: 'var(--theme-muted-text)',
                          opacity: 0.6,
                        }}
                      />
                    ))}
                  </div>
                  <span
                    className="text-xs flex-shrink-0"
                    style={{ color: 'var(--theme-muted-text)', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </span>
                  <audio
                    ref={audioRef}
                    src={audioUrl}
                    className="hidden"
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onEnded={() => {
                      setIsPlaying(false);
                      setCurrentTime(0);
                    }}
                    onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
                    onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
                    data-testid="voice-form-audio"
                  />
                </div>
              </div>
            )}
          </FormSection>

          {/* Opções */}
          <FormSection
            icon="settings"
            title="Opções"
            description="Tratamento do áudio antes da clonagem"
          >
            <Checkbox
              label="Remover ruído de fundo"
              description="Limpa o áudio antes de clonar a voz"
              checked={removeBackgroundNoise}
              onChange={setRemoveBackgroundNoise}
              data-testid="voice-form-denoise"
            />
          </FormSection>
        </div>
      </div>
    </div>
  );
};

VoiceFormPage.displayName = 'VoiceFormPage';
